import { TranslationKeys, translate } from '../../../i18n';
import { VenueAgentDto } from '../../../models/court-recording/dto/venue-agent.dto';

type VenueAgentKey = TranslationKeys extends infer K
  ? K extends `court_recording.venue_agents.${infer Rest}`
    ? Rest
    : never
  : never;

/**
 * `translate()` scoped to `court_recording.venue_agents.*`. A plain function
 * (no hook), so the pure helpers below and the components share one accessor.
 */
export const vt = (
  key: VenueAgentKey,
  params?: Record<string, string | number>,
): string =>
  translate(`court_recording.venue_agents.${key}` as TranslationKeys, params);

/**
 * Thresholds MIRRORED from the API's own heartbeat alert logic
 * (`api/src/court-recording/service/venue-agent.service.ts`). They are
 * deliberately copies rather than new numbers: the dashboard must colour a
 * value "bad" exactly when the backend would have raised the matching alert,
 * otherwise an operator sees green here while Sentry is already shouting.
 * If the API's constants move, move these with them.
 */
/** API: `OFFLINE_THRESHOLD_MS` — no heartbeat in this window = offline. */
export const STALE_LAST_SEEN_THRESHOLD_MS = 5 * 60 * 1000;
/** API: `MAX_CLOCK_SKEW_SECONDS` — uploads are hard-rejected past this. */
export const HIGH_SKEW_THRESHOLD_S = 30;
/** API: `NTP_ALERT_THRESHOLD_MS` — raises `ntp_drift`. */
export const NTP_ALERT_THRESHOLD_MS = 2000;
/** API: `DISK_ALERT_THRESHOLD_GB` — raises `disk_low`. */
export const DISK_ALERT_THRESHOLD_GB = 50;

/** Rendered in place of any telemetry field the agent has never reported. */
export const EMPTY_VALUE = '—';

export function relativeTime(iso?: string): string {
  if (!iso) return vt('time_never');
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return vt('time_in_future');
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return vt('time_seconds_ago', { count: seconds });
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return vt('time_minutes_ago', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return vt('time_hours_ago', { count: hours });
  const days = Math.floor(hours / 24);
  return vt('time_days_ago', { count: days });
}

/**
 * Humanises the agent's self-reported process uptime. Coarse on purpose —
 * the operator question is "did this box restart on me?", not "how many
 * seconds exactly", and the API already alerts on the restart itself.
 */
export function formatUptime(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) {
    return EMPTY_VALUE;
  }
  const s = Math.floor(seconds);
  if (s < 60) return `${s}s`;
  const minutes = Math.floor(s / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

export function formatDiskFree(gb?: number | null): string {
  if (gb == null || !Number.isFinite(gb)) return EMPTY_VALUE;
  return `${gb.toFixed(1)} GB`;
}

/** True when the API would have raised `disk_low` for this reading. */
export function isDiskLow(gb?: number | null): boolean {
  if (gb == null || !Number.isFinite(gb)) return false;
  return gb < DISK_ALERT_THRESHOLD_GB;
}

export function formatNtpOffset(ms?: number | null): string {
  if (ms == null || !Number.isFinite(ms)) return EMPTY_VALUE;
  return `${ms > 0 ? '+' : ''}${Math.round(ms)} ms`;
}

/** True when the API would have raised `ntp_drift` for this reading. */
export function isNtpDrifting(ms?: number | null): boolean {
  if (ms == null || !Number.isFinite(ms)) return false;
  return Math.abs(ms) > NTP_ALERT_THRESHOLD_MS;
}

export function formatCount(value?: number | null): string {
  if (value == null || !Number.isFinite(value)) return EMPTY_VALUE;
  return String(value);
}

/**
 * True when `type` is one of the alert TYPES the API reported on this agent's
 * most recently applied heartbeat.
 *
 * Takes `unknown`-ish input on purpose: `activeAlerts` is a JSON array off the
 * wire, and an older API build simply omits it. `?? []` is the whole contract
 * — a missing field means "this API cannot tell us", which must read as "no
 * alert", never as a crash inside a badge renderer.
 */
export function isAlertActive(agent: VenueAgentDto, type: string): boolean {
  return (agent.activeAlerts ?? []).includes(type);
}

/**
 * The API alert types this file matches on by hand.
 *
 * These strings are written out in `api/.../service/venue-agent.service.ts`
 * too, and nothing but this constant and the API-side spec that pins the same
 * literals (`alert types the dashboard matches on`) couples the two — the
 * projects share no module. Renaming one on the API side without the other
 * silently turns this panel green for a condition that is still firing, which
 * is the exact failure the file docblock above forbids, so the pin is on the
 * API side where the rename would happen.
 */
export const ALERT_CLOCK_UNREADABLE = 'agent_clock_unreadable';
export const ALERT_SEGMENT_SECONDS_INVALID = 'segment_seconds_invalid';

/**
 * How long the API withholds this agent's finished recordings before offering
 * them for clipping. Never re-derived here: the deployment-wide floor is a
 * server env setting the dashboard cannot see, so a locally-computed number
 * would be wrong in exactly the deployment whose operator changed it.
 */
export function formatUploadGate(seconds?: number | null): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) {
    return EMPTY_VALUE;
  }
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const minutes = Math.floor(s / 60);
  const rest = s % 60;
  return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`;
}

/**
 * True when the box reported a segment length the API REFUSED (out of the
 * 60–3600 s range, or not a number).
 *
 * The refusal is deliberately silent on the wire — a heartbeat must never
 * fail over an advisory tuning field (decision D7) — so the reported value is
 * thrown away. That is the state worth colouring: if the venue really does run
 * long segments, the gate is now too SHORT for it, and the box is back to
 * clipping a truncated tail off the end of every game that ends mid-segment.
 * Nothing else on this panel says so.
 *
 * It does NOT follow that the agent is on the deployment-wide gate — run 15.
 * The API only overwrites `lastSegmentSeconds` with a *usable* report
 * (`venue-agent.service.ts`: "leaves the last good value in place rather than
 * resetting the gate this agent is running on"), so a box that reported 900 s
 * and is now sending a bad value keeps the 960 s gate its old config bought
 * AND raises this alert. Read the gate off `uploadGateSeconds`; this predicate
 * answers whether the LATEST report was accepted, nothing more.
 */
export function isSegmentReportRefused(agent: VenueAgentDto): boolean {
  return isAlertActive(agent, ALERT_SEGMENT_SECONDS_INVALID);
}

/**
 * The operator-facing explanation of the gate rendered beside it.
 *
 * A pure function, and here rather than inline in the panel, for the reason
 * run 15 found the sentence it replaces to be false: the panel cannot see the
 * deployment-wide gate (`RECORDING_UPLOAD_GATE_SECONDS` is a server env
 * setting), so it cannot tell whether the number the API published was widened
 * for THIS box or is simply the deployment floor. The prose it replaced
 * asserted exactly that, in both directions and wrongly in both:
 *
 *  - "Widened for this box's reported 120s segments" — for any venue running
 *    segments under 300 s, `max(360, 120 + 60)` is the deployment default and
 *    nothing was widened.
 *  - "it was ignored and the deployment default applies" — for a box with an
 *    earlier ACCEPTED report the gate is still that report's, so the sentence
 *    contradicted the 16m rendered two lines above it.
 *
 * So this states facts the panel actually holds — the effective gate, the last
 * accepted segment length, whether the latest report was refused — and derives
 * nothing. That is the same rule `uploadGateSeconds` exists to enforce for the
 * NUMBER (`VenueAgentDto`: "so the panel never re-derives it from a hard-coded
 * default"), applied to the sentence explaining it.
 */
export function describeUploadGate(agent: VenueAgentDto): string {
  const gate = formatUploadGate(agent.uploadGateSeconds);
  const reported = agent.lastSegmentSeconds;

  if (isSegmentReportRefused(agent)) {
    return (
      vt('gate_refused_intro', { gate }) +
      (reported != null
        ? vt('gate_refused_from_report', { reported })
        : vt('gate_refused_default')) +
      ' ' +
      vt('gate_refused_remedy')
    );
  }

  return (
    vt('gate_normal', { gate }) +
    ' ' +
    (reported != null
      ? vt('gate_normal_reported', { reported })
      : vt('gate_normal_default'))
  );
}

/**
 * Whether this agent's courts are player-visible. Minting a key leaves an
 * agent PROVISIONED — it authenticates, heartbeats and can have cameras
 * assigned, but contributes nothing to the org's recording gates until an
 * operator activates it. `null` for a revoked agent: the health badge already
 * says REVOKED, and "live"/"not live" is meaningless for a dead key.
 */
export function recordingBadge(agent: VenueAgentDto): {
  label: string;
  color: string;
  tooltip: string;
} | null {
  if (agent.revokedAt) return null;
  if (agent.activatedAt) {
    return {
      label: vt('recording_live'),
      color: 'green',
      tooltip: vt('recording_live_tooltip', {
        date: new Date(agent.activatedAt).toLocaleDateString(),
      }),
    };
  }
  return {
    label: vt('recording_provisioned'),
    color: 'purple',
    tooltip: vt('recording_provisioned_tooltip'),
  };
}

/** Shown only for a live (non-revoked) agent that is not yet activated. */
export function needsActivation(agent: VenueAgentDto): boolean {
  return !agent.revokedAt && !agent.activatedAt;
}

/**
 * The dashboard's own gate on the Activate button: at least one court, either
 * admin-provisioned or reported by the box's heartbeat. The API deliberately
 * does not require this (an operator may activate a zero-court agent), so it
 * is a UI guard against activating a box nobody has pointed at a court yet —
 * not a rule the server enforces.
 */
export function canActivate(agent: VenueAgentDto): boolean {
  return (
    (agent.provisionedCourts?.length ?? 0) > 0 ||
    (agent.courts?.length ?? 0) > 0
  );
}

export function healthBadge(agent: VenueAgentDto): {
  label: string;
  color: string;
  tooltip: string;
} {
  if (agent.revokedAt) {
    return {
      label: vt('health_revoked'),
      color: 'red',
      tooltip: vt('health_revoked_tooltip'),
    };
  }
  if (!agent.lastSeenAt) {
    return {
      label: vt('health_never_seen'),
      color: 'gray',
      tooltip: vt('health_never_seen_tooltip'),
    };
  }
  const lastSeenMs = Date.now() - new Date(agent.lastSeenAt).getTime();
  if (lastSeenMs > STALE_LAST_SEEN_THRESHOLD_MS) {
    return {
      label: vt('health_stale'),
      color: 'orange',
      tooltip: vt('health_stale_tooltip', {
        minutes: Math.round(lastSeenMs / 60000),
      }),
    };
  }
  // Checked BEFORE the skew branch, because an unreadable clock is exactly the
  // state in which the skew reading below is NOT evidence: the API skips the
  // `lastReportedClockSkewSeconds` write whenever it could not read the
  // agent's clock header, so the value this panel would otherwise colour on is
  // the last GOOD reading — or nothing at all for a box whose RTC has been
  // dead since its first boot. Both render green.
  //
  // The consequence is not cosmetic. Since the API started refusing
  // `upload-url` for an unreadable clock, that box's clips are rejected, its
  // recordings reach FAILED, and the purchases are auto-refunded within ten
  // minutes — while this badge said "Agent is healthy". It is the exact
  // failure the file docblock above forbids, and it is invisible from the API
  // side, because over there the alert IS raised.
  if (isAlertActive(agent, ALERT_CLOCK_UNREADABLE)) {
    return {
      label: vt('health_clock_unreadable'),
      color: 'orange',
      tooltip: vt('health_clock_unreadable_tooltip'),
    };
  }
  const skew = agent.lastReportedClockSkewSeconds;
  if (skew !== undefined && skew !== null && Math.abs(skew) > HIGH_SKEW_THRESHOLD_S) {
    return {
      label: vt('health_clock_skew'),
      color: 'orange',
      tooltip: vt('health_clock_skew_tooltip', {
        skew,
        threshold: HIGH_SKEW_THRESHOLD_S,
      }),
    };
  }
  return {
    label: vt('health_online'),
    color: 'green',
    tooltip: vt('health_online_tooltip'),
  };
}

export interface CourtScopeDiff {
  /** Admin-set scope. `null` = no admin restriction (full-org access). */
  provisioned: number[] | null;
  /** Self-reported by the box over the heartbeat. `null` = never reported. */
  selfReported: number[] | null;
  /**
   * Courts the box says it is recording that the API will NOT hand it
   * uploads for. Clips for these courts are captured on the mini-PC and then
   * silently never fetched — the install-time misconfiguration that looks
   * like "recording just doesn't work on court 4".
   */
  recordingUnauthorized: number[];
  /**
   * Courts the admin provisioned that the box does not claim to record.
   * Nothing will ever be captured for them, so every purchase for those
   * courts eventually times out and refunds.
   */
  provisionedNotRecorded: number[];
  hasMismatch: boolean;
}

/**
 * Compares the box's self-reported `courts` against the admin-set
 * `provisionedCourts`.
 *
 * An empty/absent `provisionedCourts` is NOT a mismatch: the API treats it as
 * "no admin-enforced restriction" and grants full-org access (see
 * `AgentRecordingService.loadOwnedRecording`), so every self-reported court is
 * authorised. Only a non-empty admin scope can actually disagree with the box.
 */
export function courtScopeDiff(agent: VenueAgentDto): CourtScopeDiff {
  const provisioned =
    agent.provisionedCourts && agent.provisionedCourts.length > 0
      ? agent.provisionedCourts
      : null;
  const selfReported = agent.courts ?? null;

  if (!provisioned || selfReported === null) {
    return {
      provisioned,
      selfReported,
      recordingUnauthorized: [],
      provisionedNotRecorded: [],
      hasMismatch: false,
    };
  }

  const provisionedSet = new Set(provisioned);
  const selfSet = new Set(selfReported);
  const recordingUnauthorized = [...selfSet].filter((id) => !provisionedSet.has(id));
  const provisionedNotRecorded = [...provisionedSet].filter((id) => !selfSet.has(id));

  return {
    provisioned,
    selfReported,
    recordingUnauthorized,
    provisionedNotRecorded,
    hasMismatch:
      recordingUnauthorized.length > 0 || provisionedNotRecorded.length > 0,
  };
}

/** Renders a court-id list as names where known, falling back to `#id`. */
export function formatCourtList(
  ids: number[] | null | undefined,
  nameById: Map<number, string>,
): string {
  if (ids == null) return EMPTY_VALUE;
  if (ids.length === 0) return vt('court_list_none');
  return ids.map((id) => nameById.get(id) ?? `#${id}`).join(', ');
}

// ---------------------------------------------------------------------------
// Camera → court assignment (venue-setup epic, stage 3b)
// ---------------------------------------------------------------------------

export type CameraAssignmentState =
  | 'applied' // assignment exists and the box reports that camera on that court
  | 'pending' // assignment exists, box still reports the old court (applies on its next heartbeat)
  | 'unassigned' // no admin assignment; the court shown is the box's own config
  | 'no-mac' // the box has not resolved this camera's MAC yet — cannot be assigned
  | 'not-reporting'; // an assignment exists for a MAC the box is not currently reporting

export interface CameraAssignmentRow {
  /** Stable row key: the MAC when known, else `court:<id>`. */
  key: string;
  mac: string | null;
  /** Court the box currently records this camera on (from the heartbeat). */
  reportedCourtId: number | null;
  /** Court the admin assigned (from `cameraAssignments`). */
  assignedCourtId: number | null;
  connected: boolean | null;
  state: CameraAssignmentState;
}

/**
 * One row per physical camera the box reports, plus one per admin assignment
 * whose camera is not reporting. Pure so the prose and the truth table are
 * tested without Chakra (the panel's Tooltip mock drops labels).
 */
export function cameraAssignmentRows(agent: {
  cameras?: { id: string; connected: boolean; mac?: string }[];
  cameraAssignments?: Record<string, number>;
}): CameraAssignmentRow[] {
  const assignments = agent.cameraAssignments ?? {};
  const rows: CameraAssignmentRow[] = [];
  const seenMacs = new Set<string>();
  for (const camera of agent.cameras ?? []) {
    const reported = Number.parseInt(camera.id, 10);
    const reportedCourtId = Number.isFinite(reported) ? reported : null;
    const mac = camera.mac ?? null;
    if (!mac) {
      rows.push({
        key: `court:${camera.id}`,
        mac: null,
        reportedCourtId,
        assignedCourtId: null,
        connected: camera.connected,
        state: 'no-mac',
      });
      continue;
    }
    seenMacs.add(mac);
    const assigned = assignments[mac];
    const assignedCourtId = typeof assigned === 'number' ? assigned : null;
    rows.push({
      key: mac,
      mac,
      reportedCourtId,
      assignedCourtId,
      connected: camera.connected,
      state:
        assignedCourtId === null
          ? 'unassigned'
          : assignedCourtId === reportedCourtId
            ? 'applied'
            : 'pending',
    });
  }
  for (const [mac, courtId] of Object.entries(assignments)) {
    if (seenMacs.has(mac)) continue;
    rows.push({
      key: mac,
      mac,
      reportedCourtId: null,
      assignedCourtId: courtId,
      connected: null,
      state: 'not-reporting',
    });
  }
  return rows;
}

export function describeCameraAssignmentState(state: CameraAssignmentState): string {
  switch (state) {
    case 'applied':
      return vt('state_applied');
    case 'pending':
      return vt('state_pending');
    case 'unassigned':
      return vt('state_unassigned');
    case 'no-mac':
      return vt('state_no_mac');
    case 'not-reporting':
      return vt('state_not_reporting');
  }
}

